import { useState, useRef } from "react";
import { parseWordFile, extractWordsFromImage } from "../../utils/words";
import { DictionaryPopup } from "../shared/DictionaryPopup";
import { ScheduleEditor } from "../shared/ScheduleEditor";

function printWordList(list) {
  const rows = list.words.map((w, i) =>
    `<tr><td style="padding:10px 14px;font-size:18px;font-weight:700;color:#1e293b;border-bottom:1px solid #e5e7eb">${i + 1}. ${w.word}</td><td style="padding:10px 14px;border-bottom:1px solid #e5e7eb;min-width:240px">&nbsp;</td></tr>`
  ).join("");
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${list.name}</title>
  <style>body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;max-width:700px;margin:40px auto;color:#1e293b}
  h1{font-size:24px;font-weight:900;margin-bottom:4px}p{color:#64748b;font-size:14px;margin:0 0 24px}
  table{width:100%;border-collapse:collapse}th{text-align:left;padding:8px 14px;font-size:12px;text-transform:uppercase;letter-spacing:.06em;color:#94a3b8;border-bottom:2px solid #e5e7eb}
  @media print{body{margin:20px}}</style></head>
  <body><h1>${list.name}</h1>
  <p>Year ${list.yearLevel} · ${list.difficulty} · ${list.words.length} words &nbsp;|&nbsp; Printed ${new Date().toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</p>
  <table><thead><tr><th>Word</th><th>Practice (write the word)</th></tr></thead><tbody>${rows}</tbody></table></body></html>`;
  const w = window.open("", "_blank");
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 400);
}

export function PlannerWordManager({ data, onDataChange, apiKey, geminiModel }) {
  const [selList, setSelList] = useState(data.wordLists[0]?.id || null);
  const [wordIn, setWordIn] = useState("");
  const [wordDifficulty, setWordDifficulty] = useState("medium");
  const [suggestingDiff, setSuggestingDiff] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [nn, setNn] = useState(""); const [ny, setNy] = useState("6");
  const [nd, setNd] = useState("medium"); const [nc, setNc] = useState("10");
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [uploadTab, setUploadTab] = useState("file");
  const [previewWords, setPreviewWords] = useState(null);
  const [dictWord, setDictWord] = useState(null);
  const [editingListId, setEditingListId] = useState(null);
  const [editingListName, setEditingListName] = useState("");
  const [schedulingId, setSchedulingId] = useState(null);
  const fileRef = useRef();
  const imgRef = useRef();
  const list = data.wordLists.find(l => l.id === selList);

  function startRename(l, e) {
    e.stopPropagation();
    setEditingListId(l.id);
    setEditingListName(l.name);
  }

  function saveRename(id) {
    if (!editingListName.trim()) return;
    onDataChange({ ...data, wordLists: data.wordLists.map(l => l.id === id ? { ...l, name: editingListName.trim() } : l) });
    setEditingListId(null);
  }

  function cancelRename() { setEditingListId(null); }

  function dedupe(incoming) {
    const existing = new Set((list?.words || []).map(w => w.word.toLowerCase()));
    return incoming.filter(w => !existing.has(w.toLowerCase()));
  }

  async function suggestDifficulty() {
    const words = wordIn.split(/[\n,;]+/).map(w => w.trim().toLowerCase()).filter(Boolean);
    if (!words.length || !apiKey) return;
    setSuggestingDiff(true);
    try {
      const prompt = `Given these words: ${words.join(", ")}\nSuggest an overall difficulty level for a Year ${list?.yearLevel || 6} student. Reply with ONLY one word: easy, medium, or hard.`;
      const res = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${geminiModel || "gemini-1.5-flash"}:generateContent?key=${apiKey}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] }),
      });
      const json = await res.json();
      const suggestion = json?.candidates?.[0]?.content?.parts?.[0]?.text?.trim().toLowerCase();
      if (["easy","medium","hard"].includes(suggestion)) setWordDifficulty(suggestion);
    } catch { /* silently fail */ }
    setSuggestingDiff(false);
  }

  function addWords() {
    if (!wordIn.trim() || !list) return;
    const ws = dedupe(wordIn.split(/[\n,;]+/).map(w => w.trim().toLowerCase()).filter(Boolean));
    if (ws.length === 0) { setWordIn(""); return; }
    onDataChange({ ...data, wordLists: data.wordLists.map(l => l.id === selList ? { ...l, words: [...l.words, ...ws.map((w, i) => ({ id: `w_${Date.now()}_${i}`, word: w, difficulty: wordDifficulty }))] } : l) });
    setWordIn(""); setWordDifficulty("medium");
  }
  function delWord(id) { onDataChange({ ...data, wordLists: data.wordLists.map(l => l.id === selList ? { ...l, words: l.words.filter(w => w.id !== id) } : l) }); }
  function createList() {
    if (!nn.trim()) return;
    const nl = { id: `list_${Date.now()}`, name: nn, yearLevel: parseInt(ny), difficulty: nd, dailyCount: parseInt(nc), active: true, words: [] };
    onDataChange({ ...data, wordLists: [...data.wordLists, nl] });
    setSelList(nl.id); setNn(""); setShowNew(false);
  }
  function toggleActive(id) { onDataChange({ ...data, wordLists: data.wordLists.map(l => l.id === id ? { ...l, active: !l.active } : l) }); }
  function saveSchedule(id, activeFrom, activeTo) {
    onDataChange({ ...data, wordLists: data.wordLists.map(l => l.id === id ? { ...l, activeFrom: activeFrom || null, activeTo: activeTo || null } : l) });
    setSchedulingId(null);
  }
  function isListCurrentlyActive(l) {
    if (!l.active) return false;
    const now = Date.now();
    if (l.activeFrom && now < new Date(l.activeFrom).getTime()) return false;
    if (l.activeTo   && now > new Date(l.activeTo).getTime() + 86400000) return false;
    return true;
  }
  function delList(id) {
    if (!window.confirm("Delete this list?")) return;
    onDataChange({ ...data, wordLists: data.wordLists.filter(l => l.id !== id) });
    setSelList(data.wordLists.find(l => l.id !== id)?.id || null);
  }

  async function handleFileUpload(file) {
    if (!file || !list) return;
    setUploading(true); setUploadMsg(""); setPreviewWords(null);
    const words = await parseWordFile(file);
    if (words.length === 0) { setUploadMsg("⚠️ No words found. Try a .txt file with one word per line."); setUploading(false); return; }
    const unique = dedupe(words);
    setPreviewWords(unique);
    setUploadMsg(`✅ Found ${unique.length} words — review below then confirm.`);
    setUploading(false);
  }

  async function handleImageUpload(file) {
    if (!file || !list) return;
    if (!apiKey) { setUploadMsg("⚠️ No API key set. Go to Settings → Anthropic API Key and add your key first."); return; }
    setUploading(true); setUploadMsg(""); setPreviewWords(null);
    try {
      const words = await extractWordsFromImage(file, apiKey, geminiModel || "gemini-1.5-flash");
      if (words.length === 0) { setUploadMsg("⚠️ No words found in image. Try a clearer photo."); setUploading(false); return; }
      const unique = dedupe(words);
      setPreviewWords(unique);
      setUploadMsg(`✅ Found ${unique.length} words — review below then confirm.`);
    } catch (e) {
      if (e.message === "NO_KEY") setUploadMsg("⚠️ No API key set. Go to Settings → Anthropic API Key.");
      else setUploadMsg("⚠️ Could not read image. Please try again.");
    }
    setUploading(false);
  }

  function confirmPreview() {
    if (!previewWords || !list) return;
    const unique = dedupe(previewWords);
    onDataChange({ ...data, wordLists: data.wordLists.map(l => l.id === selList ? { ...l, words: [...l.words, ...unique.map((w, i) => ({ id: `w_${Date.now()}_${i}`, word: w }))] } : l) });
    setPreviewWords(null);
    setUploadMsg(`✅ ${unique.length} words added to list.`);
  }

  function removePreviewWord(w) { setPreviewWords(p => p.filter(x => x !== w)); }

  return (
    <>
      {dictWord && <DictionaryPopup word={dictWord} onClose={() => setDictWord(null)} />}
      <div className="index-card rounded-xl p-5 hover-lift">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-handwritten text-2xl text-stone-800">Word Lists</h3>
          <button className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 flex items-center gap-2" onClick={() => setShowNew(s => !s)}>+ New List</button>
        </div>
        {showNew && (
          <div className="bg-stone-100 rounded-lg p-4 mb-4 border-2 border-dashed border-amber-300">
            <div className="flex flex-col gap-2.5">
              <input className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" placeholder="List name (e.g. Week 4 Term 2)" value={nn} onChange={e => setNn(e.target.value)} />
              <div className="flex gap-2.5 flex-wrap">
                <select className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" value={ny} onChange={e => setNy(e.target.value)}><option value="6">Year 6</option><option value="8">Year 8</option></select>
                <select className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" value={nd} onChange={e => setNd(e.target.value)}><option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option></select>
                <select className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" value={nc} onChange={e => setNc(e.target.value)}>{[5,8,10,12,15,20].map(n => <option key={n} value={n}>{n} words/day</option>)}</select>
              </div>
              <button className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600" onClick={createList}>Create List</button>
            </div>
          </div>
        )}
        {data.wordLists.map(l => (
          <div key={l.id}>
          <div className={`index-card rounded-lg p-4 hover-lift cursor-pointer border-l-4 border-amber-400 group mb-2 ${selList === l.id ? 'ring-2 ring-amber-300' : ''}`} onClick={() => { if (editingListId !== l.id) setSelList(l.id); }}>
            <div className="flex-1 min-w-0">
              {editingListId === l.id ? (
                <div className="flex gap-1.5 items-center" onClick={e => e.stopPropagation()}>
                  <input
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    value={editingListName}
                    autoFocus
                    onChange={e => setEditingListName(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") saveRename(l.id); if (e.key === "Escape") cancelRename(); }}
                  />
                  <button className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600" onClick={() => saveRename(l.id)}>✓</button>
                  <button className="px-4 py-2 bg-stone-100 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-200 border border-stone-200" onClick={cancelRename}>✕</button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-1.5">
                    <div className="font-handwritten text-lg text-stone-800">{l.name}</div>
                    <button className="bg-none border-none cursor-pointer text-sm text-stone-400 p-1 rounded hover:bg-stone-200"
                      onClick={e => startRename(l, e)} title="Rename list">✏️</button>
                  </div>
                  <div className="text-xs text-stone-600 mt-1">Year {l.yearLevel} · {l.difficulty} · {l.dailyCount} words/day · {l.words.length} words</div>
                  {(l.activeFrom || l.activeTo) && (
                    <div className="text-xs text-stone-500 mt-1">
                      📅 {l.activeFrom ? new Date(l.activeFrom).toLocaleDateString("en-AU", { day:"numeric", month:"short" }) : "Any"} → {l.activeTo ? new Date(l.activeTo).toLocaleDateString("en-AU", { day:"numeric", month:"short" }) : "Any"}
                      {!isListCurrentlyActive(l) && l.active && <span className="text-amber-600 ml-1 font-bold">⏸ Outside schedule</span>}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex gap-2.5 items-center flex-shrink-0">
              <span onClick={e => { e.stopPropagation(); toggleActive(l.id); }}
                className={`text-xs font-bold cursor-pointer px-2.5 py-1 rounded-full transition-all ${isListCurrentlyActive(l) ? "text-green-700 bg-green-100" : "text-stone-500 bg-stone-200"}`}>
                {isListCurrentlyActive(l) ? "Active" : l.active ? "Scheduled" : "Off"}
              </span>
              <button title="Set active dates" className="bg-none border border-stone-300 rounded cursor-pointer text-sm p-1 text-stone-500 hover:text-amber-600 hover:border-amber-400 transition-colors"
                onClick={e => { e.stopPropagation(); setSchedulingId(schedulingId === l.id ? null : l.id); }}>📅</button>
              <button className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600" onClick={e => { e.stopPropagation(); delList(l.id); }}>✕</button>
            </div>
          </div>
          {schedulingId === l.id && (
            <ScheduleEditor list={l} onSave={saveSchedule} onClose={() => setSchedulingId(null)} />
          )}
          </div>
        ))}
      </div>

      {list && (
        <div className="index-card rounded-xl p-5 hover-lift">
          <div className="flex justify-between items-start mb-1">
            <h3 className="font-handwritten text-2xl text-stone-800">Words in "{list.name}"</h3>
            <button className="px-4 py-2 bg-stone-100 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-200 border border-stone-200 flex-shrink-0"
              onClick={() => printWordList(list)}>🖨️ Print</button>
          </div>
          <div className="text-sm text-stone-600 mb-3">{list.words.length} words total</div>

          <div className="mb-4">
            <div className="font-bold text-xs text-stone-500 uppercase tracking-wider mb-3">Add Words from File</div>
            <div className="flex gap-2 mb-3">
              <button className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${uploadTab === "file" ? "bg-amber-500 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`} onClick={() => { setUploadTab("file"); setPreviewWords(null); setUploadMsg(""); }}>📄 Document</button>
              <button className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${uploadTab === "image" ? "bg-amber-500 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`} onClick={() => { setUploadTab("image"); setPreviewWords(null); setUploadMsg(""); }}>📷 Photo</button>
            </div>

            {uploadTab === "file" && (
              <>
                <input ref={fileRef} type="file" accept=".txt,.csv,.docx" className="hidden" onChange={e => handleFileUpload(e.target.files[0])} />
                <div className="border-2 border-dashed border-stone-300 rounded-xl p-6 text-center cursor-pointer transition-all hover:border-amber-400 hover:bg-amber-50 text-stone-500 font-medium text-sm"
                  onClick={() => fileRef.current.click()}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add("border-amber-400", "bg-amber-50"); }}
                  onDragLeave={e => { e.currentTarget.classList.remove("border-amber-400", "bg-amber-50"); }}
                  onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove("border-amber-400", "bg-amber-50"); handleFileUpload(e.dataTransfer.files[0]); }}>
                  {uploading ? "⏳ Reading file…" : "📄 Drop a .txt, .csv or .docx file here, or tap to choose"}
                </div>
              </>
            )}

            {uploadTab === "image" && (
              <>
                <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload(e.target.files[0])} />
                <div className="border-2 border-dashed border-stone-300 rounded-xl p-6 text-center cursor-pointer transition-all hover:border-amber-400 hover:bg-amber-50 text-stone-500 font-medium text-sm"
                  onClick={() => imgRef.current.click()}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add("border-amber-400", "bg-amber-50"); }}
                  onDragLeave={e => { e.currentTarget.classList.remove("border-amber-400", "bg-amber-50"); }}
                  onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove("border-amber-400", "bg-amber-50"); handleImageUpload(e.dataTransfer.files[0]); }}>
                  {uploading ? "⏳ Scanning image with AI…" : "📷 Drop a photo of your word list here, or tap to choose"}
                </div>
                <div className="text-xs text-stone-500 mt-2">Works best with printed or typed word lists. AI will extract all visible words.</div>
              </>
            )}

            {uploadMsg && <div className={`mt-2 text-sm font-semibold ${uploadMsg.startsWith("✅") ? "text-green-600" : "text-amber-600"}`}>{uploadMsg}</div>}

            {previewWords && previewWords.length > 0 && (
              <div className="mt-3 p-4 bg-stone-100 rounded-lg border border-stone-200">
                <div className="font-bold text-sm mb-2 text-stone-800">Review extracted words — remove any that don't belong:</div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {previewWords.map((w, i) => (
                    <span key={i} className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium inline-flex items-center gap-1">
                      {w}<button className="text-amber-400 hover:text-amber-700 cursor-pointer ml-1" onClick={() => removePreviewWord(w)}>×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2.5 mt-3">
                  <button className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600" onClick={confirmPreview}>✅ Add {previewWords.length} words</button>
                  <button className="px-4 py-2 bg-stone-100 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-200 border border-stone-200" onClick={() => { setPreviewWords(null); setUploadMsg(""); }}>Cancel</button>
                </div>
              </div>
            )}
          </div>

          <div className="font-bold text-xs text-stone-500 uppercase tracking-wider mb-2">Or Paste Words</div>
          <textarea className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 min-h-24" placeholder={"necessary\nreceive\nseparate"} value={wordIn} onChange={e => setWordIn(e.target.value)} />
          <div className="flex gap-2 items-center flex-wrap mt-2.5">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-bold text-stone-500">Difficulty:</span>
              {["easy","medium","hard"].map(d => (
                <button key={d} type="button" onClick={() => setWordDifficulty(d)}
                  className={`text-xs font-bold px-2 py-1 rounded border-2 transition-all cursor-pointer capitalize ${wordDifficulty === d ? "border-blue-500 bg-blue-100 text-blue-600" : "border-stone-300 bg-stone-100 text-stone-500 hover:border-stone-400"}`}>
                  {d}
                </button>
              ))}
            </div>
            {apiKey && (
              <button className="px-4 py-2 bg-stone-100 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-200 border border-stone-200"
                onClick={suggestDifficulty} disabled={!wordIn.trim() || suggestingDiff}>
                {suggestingDiff ? "⏳ Thinking…" : "✨ Auto-suggest"}
              </button>
            )}
            <button className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600" onClick={addWords}>+ Add Words</button>
          </div>

          {list.words.length > 0 && (
            <>
              <div className="font-bold mt-4 mb-2 text-xs text-stone-500 uppercase tracking-wider">Current Words</div>
              <div className="flex flex-wrap gap-2">
                {list.words.map(w => (
                  <span key={w.id} className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium inline-flex items-center gap-1">
                    {w.word}
                    <button className="dict-btn" title="Look up definition" onClick={() => setDictWord(w.word)}>📖</button>
                    <button className="text-amber-400 hover:text-amber-700 cursor-pointer ml-1" onClick={() => delWord(w.id)}>×</button>
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
